import { useVirtualizer, type Virtualizer } from "@tanstack/react-virtual";
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

export type LoadingState = "idle" | "loading" | "loading-more" | "error";

export interface UseInfiniteScrollOptions<T = unknown> {
  /** Array of items being displayed */
  items: T[];
  /** Callback to load more items (optional - if not provided, no infinite scroll) */
  onLoadMore?: () => void | Promise<void>;
  /** Whether there are more items to load */
  hasMore?: boolean;

  // Loading state (auto-detect or explicit)
  /** Auto-detects initial vs loading-more from items.length */
  isLoading?: boolean;
  /** Explicit loading state override (takes precedence) */
  loadingState?: LoadingState;

  // Virtualization options
  /** Enable virtualization (default: true for 100+ items) */
  enableVirtualization?: boolean;
  /** Estimated size of each item in pixels */
  estimatedItemSize?: number;
  /** Number of items to render outside viewport */
  overscan?: number;

  // Scroll behavior
  /** Custom scroll element (defaults to parent) */
  scrollElement?: HTMLElement | null;
  /** Distance from bottom in pixels to trigger load */
  loadMoreThreshold?: number;
}

export interface UseInfiniteScrollReturn {
  // Virtualization (null if disabled)
  virtualizer: Virtualizer<HTMLElement, Element> | null;
  virtualItems: ReturnType<Virtualizer<HTMLElement, Element>["getVirtualItems"]>;

  // Computed loading state
  computedLoadingState: LoadingState;
  isInitialLoad: boolean;
  isLoadingMore: boolean;

  // Refs
  scrollRef: RefObject<HTMLDivElement | null>;
  loadMoreRef: RefObject<HTMLDivElement | null>;

  // Utils
  scrollToIndex: (index: number, options?: { align?: "start" | "center" | "end" }) => void;
  scrollToTop: () => void;
}

/**
 * useInfiniteScroll
 *
 * Hook for implementing infinite scroll with optional virtualization.
 * Intelligently detects initial vs loading-more states from context.
 *
 * Features:
 * - Smart loading state detection (initial vs loading-more)
 * - Optional explicit state control
 * - TanStack Virtual integration for performance
 * - Intersection Observer for load triggering
 * - Configurable virtualization threshold
 *
 * @example
 * ```tsx
 * const { scrollRef, computedLoadingState, isLoadingMore } = useInfiniteScroll({
 *   items,
 *   onLoadMore: fetchNextPage,
 *   hasMore: hasNextPage,
 *   isLoading: isFetching,
 * });
 * ```
 */
export function useInfiniteScroll<T = unknown>({
  items,
  onLoadMore,
  hasMore,
  isLoading = false,
  loadingState,
  enableVirtualization,
  estimatedItemSize = 50,
  overscan = 5,
  scrollElement,
  loadMoreThreshold = 100,
}: UseInfiniteScrollOptions<T>): UseInfiniteScrollReturn {
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);
  const [refsReady, setRefsReady] = useState(0);

  // Track when refs are populated
  useEffect(() => {
    if (loadMoreRef.current && (scrollElement || scrollRef.current)) {
      setRefsReady((prev) => prev + 1);
    }
    // `hasMore` and the loading state sit alongside `items.length` because consumers mount
    // the sentinel conditionally — `DataTable` renders it only while
    // `onLoadMore && hasMore && !isInitialLoad` — so it can appear for reasons that never
    // change the row count: a query resolving `hasMore` to true, or the first page
    // finishing. Without them the observer effect below stays parked on the null `target`
    // it saw first and infinite scroll never starts for that table.
    //
    // `onLoadMore` is deliberately NOT a dependency: callers commonly pass a fresh closure
    // every render, which would re-run this constantly and, through `refsReady`, tear the
    // observer down and rebuild it on each one.
  }, [scrollElement, items.length, hasMore, isLoading, loadingState]);

  // Compute loading state (explicit takes precedence)
  const computedLoadingState: LoadingState = loadingState
    ? loadingState
    : isLoading
      ? items.length === 0
        ? "loading"
        : "loading-more"
      : "idle";

  const isInitialLoad = computedLoadingState === "loading";
  const isLoadingMore = computedLoadingState === "loading-more";

  // Auto-enable virtualization for large lists
  const shouldVirtualize = enableVirtualization !== undefined ? enableVirtualization : items.length >= 100;

  // Always call virtualizer hook (React rules), but conditionally expose it
  const tempVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollElement || scrollRef.current,
    estimateSize: () => estimatedItemSize,
    overscan,
  });

  // Only expose virtualizer if enabled
  const virtualizer = shouldVirtualize ? tempVirtualizer : null;
  const virtualItems = shouldVirtualize ? tempVirtualizer.getVirtualItems() : [];

  /**
   * Live view of the state the observer callback must consult.
   *
   * Read through a ref rather than captured in the effect's closure so the observer
   * itself can stay mounted across a page load. Tearing it down while a page is in
   * flight and rebuilding it afterwards looks equivalent and is not: a fresh
   * `IntersectionObserver` only reports a *change* in intersection, and the sentinel
   * that triggered the load is still intersecting when the page lands, so the new
   * observer sees no edge and never fires again. That stalls paging permanently a
   * page or two in — a 25,063-row table that stops at 100 with `hasMore` still true.
   */
  const gateRef = useRef({ hasMore, isInitialLoad, isLoadingMore, onLoadMore });
  gateRef.current = { hasMore, isInitialLoad, isLoadingMore, onLoadMore };

  /**
   * The current `maybeLoad`, published by the observer effect so the post-page effect
   * below can run the same check without duplicating the geometry test or the guards.
   */
  const checkRef = useRef<(() => void) | null>(null);

  // Intersection Observer for load trigger
  useEffect(() => {
    const target = loadMoreRef.current;
    const scrollContainer = scrollElement || scrollRef.current;

    // Only structural inputs gate construction here. Whether a load may fire *right
    // now* is decided per-event below, against `gateRef`.
    if (!target || !scrollContainer) {
      return;
    }

    /**
     * Measures rather than trusting an intersection edge.
     *
     * `IntersectionObserver` reports only *changes*, and the sentinel that triggered a
     * load is usually still visible when the page lands — no edge, no callback. Reading
     * the geometry means every trigger source asks the same question ("is the sentinel
     * within the threshold right now?") and gets an answer that does not depend on
     * whether an edge happened to occur.
     */
    const maybeLoad = () => {
      const gate = gateRef.current;
      // `loadingRef` is the synchronous half of the guard: `isLoadingMore` only turns
      // true once the caller has re-rendered, which is later than the next event can
      // arrive. Both halves are needed.
      if (loadingRef.current) return;
      if (!gate.hasMore || gate.isInitialLoad || gate.isLoadingMore || !gate.onLoadMore) return;

      // Read live: a sentinel the consumer has since remounted is detached and measures as an all-zero rect.
      const sentinel = loadMoreRef.current;
      if (!sentinel?.isConnected) return;

      const targetRect = sentinel.getBoundingClientRect();
      const rootRect = scrollContainer.getBoundingClientRect();
      if (targetRect.top > rootRect.bottom + loadMoreThreshold) return;

      loadingRef.current = true;
      // The call is made INSIDE the promise chain rather than passed to
      // `Promise.resolve(...)`. A synchronous throw from `onLoadMore` would otherwise
      // escape before `finally` was ever attached, stranding `loadingRef` at `true` and
      // killing paging for good — the same latch this guard exists to prevent. The
      // `catch` covers a rejected promise for the same reason, and keeps it from
      // surfacing as an unhandled rejection.
      void Promise.resolve()
        .then(() => gate.onLoadMore?.())
        .catch((error) => {
          console.error("Infinite-scroll load failed.", error);
        })
        .finally(() => {
          loadingRef.current = false;
        });
    };

    const observer = new IntersectionObserver(() => maybeLoad(), {
      root: scrollContainer,
      rootMargin: `0px 0px ${loadMoreThreshold}px 0px`,
      threshold: 0,
    });

    observer.observe(target);

    /**
     * The observer alone is not enough, and this listener is not a belt-and-braces
     * duplicate of it.
     *
     * Once the sentinel is statically visible — a viewport taller than the page just
     * appended, so it never leaves the trigger margin — the observer has no further
     * edges to report and paging stops dead with `hasMore` still true (observed: a
     * 25,063-row table frozen at 100 rows, scrolled to the bottom, zero requests).
     * Scroll events keep arriving in exactly that state, so they are what carries
     * paging forward; `maybeLoad` re-checks the geometry and the guards on each one,
     * so this cannot run away.
     */
    scrollContainer.addEventListener("scroll", maybeLoad, { passive: true });
    checkRef.current = maybeLoad;

    return () => {
      observer.disconnect();
      scrollContainer.removeEventListener("scroll", maybeLoad);
      checkRef.current = null;
    };
  }, [scrollElement, loadMoreThreshold, refsReady]);

  /**
   * Continue after a page lands.
   *
   * When the appended page is shorter than the viewport the sentinel is still within
   * the trigger margin and no scroll event follows, so neither trigger above fires and
   * the list stalls with rows left. Re-checking once per settled page closes that gap;
   * the geometry test inside `checkRef` is what stops it from paging on forever.
   */
  useEffect(() => {
    if (isLoadingMore || isInitialLoad) return;
    checkRef.current?.();
  }, [isInitialLoad, isLoadingMore, items.length, hasMore]);

  // Scroll utilities
  const scrollToIndex = useCallback(
    (index: number, options?: { align?: "start" | "center" | "end" }) => {
      if (virtualizer) {
        virtualizer.scrollToIndex(index, options);
      } else if (scrollRef.current) {
        const element = scrollRef.current.children[index] as HTMLElement | undefined;
        element?.scrollIntoView({
          behavior: "smooth",
          block: options?.align || "start",
        });
      }
    },
    [virtualizer]
  );

  const scrollToTop = useCallback(() => {
    if (virtualizer) {
      virtualizer.scrollToIndex(0, { align: "start" });
    } else if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [virtualizer]);

  return {
    virtualizer,
    virtualItems,
    computedLoadingState,
    isInitialLoad,
    isLoadingMore,
    scrollRef,
    loadMoreRef,
    scrollToIndex,
    scrollToTop,
  };
}
